import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, isPositive } from '../../lib/money';
import type { PrintSize } from './print-types';
import type { PrintLine } from './types';

/** "2.000" prints as "2"; "1.500" as "1.5". A string operation: quantities are decimals. */
export function formatQty(qty: string): string {
  return qty.includes('.') ? qty.replace(/\.?0+$/, '') : qty;
}

/** Item lines. A4 is a table; 80mm is one column so nothing is cut off a narrow roll. */
export function PrintLines({ lines, size }: { lines: PrintLine[]; size: PrintSize }): ReactNode {
  const { t } = useTranslation();
  if (size === '80mm') {
    return (
      <div className="print-lines">
        {lines.map((l, i) => (
          <div key={`${l.description}-${String(i)}`} className="print-line">
            <div>{l.description}</div>
            <div className="print-row">
              <span>
                {formatQty(l.qty)} × {formatMoney(l.unitPrice)}
              </span>
              <span className="print-amount">{formatMoney(l.lineTotal)}</span>
            </div>
            {isPositive(l.discountAmount) ? (
              <div className="print-row print-small">
                <span>{t('print.discount')}</span>
                <span className="print-amount">-{formatMoney(l.discountAmount)}</span>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  }
  return (
    <table className="print-table">
      <thead>
        <tr>
          <th>{t('print.col.description')}</th>
          <th className="print-num">{t('print.col.qty')}</th>
          <th className="print-num">{t('print.col.unitPrice')}</th>
          <th className="print-num">{t('print.col.discount')}</th>
          <th className="print-num">{t('print.col.amount')}</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={`${l.description}-${String(i)}`}>
            <td>{l.description}</td>
            <td className="print-num">{formatQty(l.qty)}</td>
            <td className="print-num">{formatMoney(l.unitPrice)}</td>
            <td className="print-num">{formatMoney(l.discountAmount)}</td>
            <td className="print-num">{formatMoney(l.lineTotal)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
